import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiClient } from '../services/apiClient';
import { API_ENDPOINTS } from '../services/endpoints';
import { getCurrentChatToolbarTarget } from '../services/wecomSDK';
import type { WeComChatToolbarTarget } from '../services/wecomSDK';
import {
  WeComConversationInheritOpenDataFrame,
  type InheritRelatedConversation,
} from '../components/conversation/WeComConversationInheritOpenDataFrame';
import { normalizeUtcDateTimeValue } from '../utils/format';
import type {
  Conversation,
  Message,
  MessageDirection,
  MessageQueryParams,
  MessageType,
} from '../types/conversation';

const adaptMessage = (raw: Record<string, unknown>): Message => {
  const senderId = raw.sender_id;
  const direction: MessageDirection =
    senderId === null || senderId === undefined || String(senderId).trim() === '' ? 'receive' : 'send';
  const msgTime = raw.msg_time;

  let contentJsonObj: Record<string, unknown> | undefined;
  if (raw.content_json && typeof raw.content_json === 'object') {
    contentJsonObj = raw.content_json as Record<string, unknown>;
  } else if (typeof raw.content_json === 'string') {
    try {
      contentJsonObj = JSON.parse(raw.content_json);
    } catch {
      contentJsonObj = undefined;
    }
  }

  return {
    id: String(raw.id ?? raw.msg_id ?? raw.wecom_msgid ?? ''),
    conversation_id: String(raw.conversation_id ?? ''),
    msg_id: (raw.wecom_msgid as string) || (raw.msg_id as string) || '',
    msg_type: (raw.msg_type as MessageType) || 'text',
    direction,
    sender_type: String(raw.sender_type || '') || undefined,
    sender_id: String(raw.sender_id ?? ''),
    sender_name: (raw.sender_name as string) || '',
    sender_wecom_userid: (raw.sender_wecom_userid as string) || undefined,
    sender_open_userid: (raw.sender_open_userid as string) || undefined,
    sender_avatar: (raw.sender_avatar as string) || undefined,
    receiver_type: String(raw.receiver_type || '') || undefined,
    receiver_wecom_userid: (raw.receiver_wecom_userid as string) || undefined,
    receiver_open_userid: (raw.receiver_open_userid as string) || undefined,
    content: (raw.content as string) || '',
    content_json: contentJsonObj,
    media_url: (raw.media_url as string) || undefined,
    media_size: (raw.media_size as number) || undefined,
    media_duration: (raw.media_duration as number) || undefined,
    file_name: (raw.file_name as string) || undefined,
    file_size: (raw.media_size as number) || undefined,
    duration: (raw.media_duration as number) || undefined,
    send_time: typeof msgTime === 'string' || typeof msgTime === 'number'
      ? normalizeUtcDateTimeValue(msgTime)
      : '',
    msg_time_raw: typeof msgTime === 'number' ? msgTime : undefined,
    is_revoked: Boolean(raw.is_recalled),
    risk_flagged: Boolean(raw.has_risk),
    risk_types: (raw.risk_types as string[]) || undefined,
  };
};

const getFallbackChatTarget = (): WeComChatToolbarTarget | null => {
  const params = new URLSearchParams(window.location.search);
  const externalUserId = params.get('external_userid');
  if (externalUserId) return { type: 'single', externalUserId };
  const chatId = params.get('chat_id') || params.get('chatId');
  return chatId ? { type: 'group', chatId } : null;
};

const WeComConversationInheritSidebarPage: React.FC = () => {
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [relatedConversations, setRelatedConversations] = useState<InheritRelatedConversation[]>([]);
  const [activeTab, setActiveTab] = useState<'single' | 'group'>('single');
  const [loadedTab, setLoadedTab] = useState<'single' | 'group' | null>(null);
  const [selectedConversation, setSelectedConversation] = useState<InheritRelatedConversation | null>(null);
  const [listLoading, setListLoading] = useState(false);
  const [listRefreshing, setListRefreshing] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [contextError, setContextError] = useState('');
  const [messageError, setMessageError] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [msgPage, setMsgPage] = useState(1);
  const [keyword, setKeyword] = useState('');
  const [debouncedKeyword, setDebouncedKeyword] = useState('');
  const relatedRequestSeqRef = useRef(0);
  const messageRequestSeqRef = useRef(0);
  const relatedAbortRef = useRef<AbortController | null>(null);
  const relatedCacheRef = useRef<Map<string, InheritRelatedConversation[]>>(new Map());

  const resolveChatTarget = useCallback(async () => (
    await getCurrentChatToolbarTarget() || getFallbackChatTarget()
  ), []);

  const fetchConversation = useCallback(async () => {
    relatedRequestSeqRef.current += 1;
    relatedAbortRef.current?.abort();
    relatedAbortRef.current = null;
    relatedCacheRef.current.clear();
    setRelatedConversations([]);
    setLoadedTab(null);
    setListLoading(false);
    setListRefreshing(false);
    setLoading(true);
    setContextError('');
    try {
      const target = await resolveChatTarget();
      if (!target) {
        setConversation(null);
        setContextError('无法识别当前聊天对象');
        return;
      }
      const response = target.type === 'group'
        ? await apiClient.get(API_ENDPOINTS.CONVERSATIONS.CONVERSATION_INHERIT_SIDEBAR_BY_ROOM, {
          params: { chat_id: target.chatId },
          suppressErrorToast: true,
        } as any)
        : await apiClient.get(API_ENDPOINTS.CONVERSATIONS.CONVERSATION_INHERIT_SIDEBAR_BY_EXTERNAL_CONTACT, {
          params: { external_userid: target.externalUserId },
          suppressErrorToast: true,
        } as any);
      const data = response.data || response;
      setConversation(data as Conversation);
      setSelectedConversation(null);
    } catch (err: any) {
      setConversation(null);
      setMessages([]);
      setContextError(err?.response?.data?.detail || err?.message || '未找到当前客户的会话存档记录');
    } finally {
      setLoading(false);
    }
  }, [resolveChatTarget]);

  const fetchRelatedConversations = useCallback(async (
    chatType: 'single' | 'group',
    options: { force?: boolean; keepCurrent?: boolean } = {},
  ) => {
    const externalUserId = conversation?.external_userid;
    if (!externalUserId) return;

    const cacheKey = `${externalUserId}:${chatType}`;
    const cachedRows = options.force ? undefined : relatedCacheRef.current.get(cacheKey);
    relatedRequestSeqRef.current += 1;
    const requestSeq = relatedRequestSeqRef.current;
    relatedAbortRef.current?.abort();

    if (cachedRows) {
      setRelatedConversations(cachedRows);
      setLoadedTab(chatType);
      setListLoading(false);
      return;
    }

    const controller = new AbortController();
    relatedAbortRef.current = controller;
    if (options.keepCurrent) {
      setListRefreshing(true);
    } else {
      setRelatedConversations([]);
      setLoadedTab(null);
      setListLoading(true);
    }
    try {
      const response = await apiClient.get(
        API_ENDPOINTS.CONVERSATIONS.CONVERSATION_INHERIT_SIDEBAR_RELATED_CONVERSATIONS,
        {
          params: { external_userid: externalUserId, chat_type: chatType },
          signal: controller.signal,
          suppressErrorToast: true,
        } as any,
      );
      if (controller.signal.aborted || requestSeq !== relatedRequestSeqRef.current) return;
      const data = response.data || response;
      const rows = (data || []) as InheritRelatedConversation[];
      relatedCacheRef.current.set(cacheKey, rows);
      setRelatedConversations(rows);
      setLoadedTab(chatType);
    } catch (err: any) {
      if (controller.signal.aborted || requestSeq !== relatedRequestSeqRef.current || err?.code === 'ERR_CANCELED') return;
      if (!options.keepCurrent) {
        setRelatedConversations([]);
        setLoadedTab(chatType);
      }
    } finally {
      if (requestSeq === relatedRequestSeqRef.current) {
        relatedAbortRef.current = null;
        setListRefreshing(false);
        setListLoading(false);
      }
    }
  }, [conversation?.external_userid]);

  const handleTabChange = useCallback((chatType: 'single' | 'group') => {
    if (!conversation) {
      setListLoading(false);
      setListRefreshing(false);
      return;
    }
    if (chatType === activeTab || conversation.type === 'group') return;
    relatedRequestSeqRef.current += 1;
    relatedAbortRef.current?.abort();
    relatedAbortRef.current = null;
    setListRefreshing(false);
    setRelatedConversations([]);
    setLoadedTab(null);
    setListLoading(true);
    setKeyword('');
    setDebouncedKeyword('');
    setActiveTab(chatType);
  }, [activeTab, conversation]);

  const handleRefreshList = useCallback(() => {
    if (conversation?.type !== 'single' || !conversation.external_userid) return;
    relatedCacheRef.current.delete(`${conversation.external_userid}:${activeTab}`);
    fetchRelatedConversations(activeTab, { force: true, keepCurrent: true });
  }, [activeTab, conversation?.external_userid, conversation?.type, fetchRelatedConversations]);

  const fetchMessages = useCallback(async (page: number) => {
    if (!selectedConversation?.id) return;
    const requestSeq = ++messageRequestSeqRef.current;
    setMessagesLoading(true);
    setMessageError('');
    try {
      const searchKeyword = debouncedKeyword.trim();
      const effectiveKeyword = searchKeyword.length >= 2 ? searchKeyword : '';
      const params: MessageQueryParams = {
        page,
        page_size: 50,
        keyword: effectiveKeyword || undefined,
      };
      const response = await apiClient.get(
        API_ENDPOINTS.CONVERSATIONS.CONVERSATION_INHERIT_SIDEBAR_MESSAGES(selectedConversation.id),
        { params, suppressErrorToast: true } as any,
      );
      const data = response.data || response;
      const rawItems = (data.items || []) as Record<string, unknown>[];
      const reversed = rawItems.map(adaptMessage).reverse();
      if (requestSeq !== messageRequestSeqRef.current) return;
      setMessages((previous) => (page === 1 ? reversed : [...reversed, ...previous]));
      setHasMore(Boolean(data.pagination?.has_next));
      setMsgPage(page);
    } catch {
      if (requestSeq !== messageRequestSeqRef.current) return;
      setMessageError('消息加载失败，请稍后重试');
    } finally {
      if (requestSeq === messageRequestSeqRef.current) {
        setMessagesLoading(false);
      }
    }
  }, [debouncedKeyword, selectedConversation?.id]);

  const handleConversationSelect = useCallback((id: string) => {
    const target = relatedConversations.find((item) => String(item.id) === String(id));
    if (!target) return;
    messageRequestSeqRef.current += 1;
    setKeyword('');
    setDebouncedKeyword('');
    setMessages([]);
    setSelectedConversation(target);
  }, [relatedConversations]);

  const handleBack = useCallback(() => {
    messageRequestSeqRef.current += 1;
    setSelectedConversation(null);
    setMessages([]);
    setMessageError('');
    setKeyword('');
    setDebouncedKeyword('');
  }, []);

  const handleLoadMoreMessages = useCallback(() => {
    if (messagesLoading || !hasMore || !selectedConversation?.id) return;
    fetchMessages(msgPage + 1);
  }, [fetchMessages, hasMore, messagesLoading, msgPage, selectedConversation?.id]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedKeyword(keyword), 350);
    return () => window.clearTimeout(timer);
  }, [keyword]);

  useEffect(() => {
    fetchConversation();
  }, [fetchConversation]);

  useEffect(() => {
    if (!conversation) return;
    if (conversation.type === 'group') {
      setActiveTab('group');
      const currentGroup: InheritRelatedConversation = {
        id: conversation.id,
        type: 'group',
        name: conversation.group_name || conversation.external_name || conversation.wecom_room_id || '当前客户群',
        avatar: conversation.external_avatar,
        message_count: conversation.message_count,
        today_count: conversation.today_count || 0,
        last_message: conversation.last_message || '',
        last_message_time: conversation.last_message_time,
        wecom_room_id: conversation.wecom_room_id,
        group_member_count: conversation.group_member_count ?? undefined,
      };
      setRelatedConversations([currentGroup]);
      setLoadedTab('group');
      setListLoading(false);
      setSelectedConversation(currentGroup);
      return;
    }
    setActiveTab('single');
  }, [conversation]);

  useEffect(() => {
    if (conversation?.type === 'single') fetchRelatedConversations(activeTab);
  }, [activeTab, conversation?.type, fetchRelatedConversations]);

  useEffect(() => {
    if (selectedConversation) fetchMessages(1);
  }, [fetchMessages, selectedConversation]);

  useEffect(() => {
    if (!contextError) return undefined;
    setListLoading(false);
    setListRefreshing(false);
    const timer = window.setTimeout(() => setContextError(''), 2600);
    return () => window.clearTimeout(timer);
  }, [contextError]);

  useEffect(() => () => {
    relatedRequestSeqRef.current += 1;
    messageRequestSeqRef.current += 1;
    relatedAbortRef.current?.abort();
  }, []);

  const displayedConversations = loadedTab === activeTab
    ? relatedConversations.filter((item) => item.name.includes(keyword.trim()))
    : [];

  return (
    <WeComConversationInheritOpenDataFrame
      conversation={conversation}
      conversations={displayedConversations}
      selectedConversation={selectedConversation}
      activeTab={activeTab}
      keyword={keyword}
      loading={loading}
      listLoading={listLoading}
      listRefreshing={listRefreshing}
      messagesLoading={messagesLoading}
      contextError={contextError}
      messageError={messageError}
      messages={messages}
      hasMore={hasMore}
      onRefresh={handleRefreshList}
      onTabChange={handleTabChange}
      onKeywordChange={setKeyword}
      onConversationSelect={handleConversationSelect}
      onBack={handleBack}
      onLoadMore={handleLoadMoreMessages}
      onRetryMessages={() => fetchMessages(1)}
    />
  );
};

export default WeComConversationInheritSidebarPage;
